import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { environment } from '../../../environments/environment';

import { AuthService } from '@services/auth.service';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from 'src/app/services/support/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from '@services/support/loader.service';
import { MultilanguageService } from '@services/support/multilanguage.service';
import { Language } from '@models/Language';
import { DbService } from '@services/db.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent implements OnInit {

  // Login form object
  @ViewChild('loginForm', { static: true }) form: NgForm;

  // Login form binded data
  loginUser = {
    account: '',
    username: '',
    password: ''
  }

  bgStyle = {}

  // Booleans for enable and disable login button
  isLoginProgress: boolean = false;
  isFormValid: boolean = false;

  authSubscription: Subscription = null;

  moreOptionsOpen: boolean = false
  accountNameDontRemeber: boolean = false;
  useChinaServer: boolean = false;

  verification = {
    open: false,
    code: null
  };
  verifyUser: {
    account: string,
    username: string,
    password: string
  };

  msLoginUrl: string = null;
  msLoginMode: boolean = false;

  oidcLoginUrl: string = null;

  currentLang: Language;
  allLangs: Language[] = [];
  showLangs: boolean = false;
  langSub: Subscription = null;
  isAdmin = true

  constructor(
    private authService: AuthService,
    private dbService: DbService,
    private router: Router,
    private route: ActivatedRoute,
    private multilanguageService: MultilanguageService,
    private loaderService: LoaderService,
    private snackBarService: SnackbarService,
    private translateService: TranslateService,
    private elem: ElementRef
  ) {
    this.route.params.subscribe(params => {
      this.loginUser.account = params['account_name'];
    });
  }

  ngOnInit() {
    if (environment.azureAdConfig && environment.azureAdConfig['name']) {
      this.msLoginUrl = this.renderTemplate(environment.azureAdConfig.msLoginUrl, { name: environment.azureAdConfig['name'] })
    }

    if (environment.oidcConfig && environment.oidcConfig['name']) {
      this.oidcLoginUrl = this.renderTemplate(environment.oidcConfig.oidcLoginUrl, { name: environment.oidcConfig['name'] })
    }

    this.authSubscription = this.authService.getAuth().subscribe(auth => {
      if (auth) {
        let returnUrl: string = this.route.snapshot.queryParams['returnUrl']
        if (returnUrl) {
            this.router.navigateByUrl(returnUrl)
            return
        }
        this.router.navigate(['/'])
      }
    });
    this.useChinaServer = this.dbService.getSourceType() === "socket";

    const savedAccountName = localStorage.getItem('vsightWorkflowAccountName');
    if (savedAccountName) {
      this.accountNameDontRemeber = false;
      this.loginUser.account = savedAccountName;
    }

    // Listen for form status
    this.form.statusChanges.subscribe(result => {
      if (result == 'VALID') {
        this.isFormValid = true;
      } else {
        this.isFormValid = false;
      }
    });

    this.currentLang = this.multilanguageService.currentLang;
    this.allLangs = this.multilanguageService.allLangs;
    this.langSub = this.multilanguageService.onLangChange.subscribe(change => {
      this.currentLang = change.lang;
    });

    const height = this.elem.nativeElement.offsetHeight
    this.bgStyle = {
      width: `${height*2}px`,
      height: `${height*2}px`,
      top: `${-height/2}px`,
      left: `${-height*2/3}px`
    }
  }

  chinaServerChange(useChina: boolean) {
    this.snackBarService.error("Not supported yet")
    //this.dbService.setSourceType(useChina ? "socket" : "firebase");
  }

  onLanguageSelected(lang: Language) {
    this.multilanguageService.setCurrentLanguage(lang.code);
  }

  ngOnDestroy() {
    if (this.authSubscription) { this.authSubscription.unsubscribe() }
    if (this.langSub) { this.langSub.unsubscribe() }
  }

  renderTemplate(templateStr: string, args: any): string {
    templateStr = templateStr.replace(/`/g, '\\`');
    const keys = Object.keys(args);
    const fn = new Function(...keys, 'return `' + templateStr + '`');
    return fn(...keys.map(key => args[key]));
  }

  loginWithMicrosoft(form: NgForm) {
    if (this.msLoginUrl) {
      window.location.href = this.msLoginUrl;
    } else {
      if (form.value.account && form.value.account.length >= 2) {
        if (!this.accountNameDontRemeber) { localStorage.setItem('vsightWorkflowAccountName', form.value.account) }
        else { localStorage.removeItem('vsightWorkflowAccountName') }

        window.location.href = this.renderTemplate(environment.azureAdConfig.msLoginUrl, { name: form.value.account });
      } else {
        this.msLoginMode = true;
        form.controls['account'].markAsTouched();
      }
    }
  }

  loginWithOidc(form: NgForm) {
    if (this.oidcLoginUrl) {
      window.location.href = this.oidcLoginUrl;
    } else {
      if (form.value.account && form.value.account.length >= 2) {
        if (!this.accountNameDontRemeber) { localStorage.setItem('vsightWorkflowAccountName', form.value.account) }
        else { localStorage.removeItem('vsightWorkflowAccountName') }

        window.location.href = this.renderTemplate(environment.oidcConfig.oidcLoginUrl, { name: form.value.account });
      } else {
        this.msLoginMode = true;
        form.controls['account'].markAsTouched();
      }
    }
  }

  onLogin(form: NgForm) {
    if (!navigator.onLine) {
      this.translateService.get('LOGIN.NO-INTERNET-ERROR').subscribe(result => {
        this.snackBarService.error(result);
      });
    } else {
      // Start login process if form valid
      // Form always valid. When form not valid -> Login button disabled
      this.isLoginProgress = true;
      this.loaderService.show();

      this.authService.login(form.value.account, form.value.username, form.value.password)
      .then(result => {
        if (result.status && result.status === "verification-sended") {
          this.verifyUser = {
            account: form.value.account,
            username: form.value.username,
            password: form.value.password
          };
          this.verification.open = true;
          this.loaderService.hide();
          this.isLoginProgress = false;
        } else {
          this.authService.loginToFirebase(result.token)
          .then(() => {
            if (!this.accountNameDontRemeber) { localStorage.setItem('vsightWorkflowAccountName', this.loginUser.account) }
            else { localStorage.removeItem('vsightWorkflowAccountName') }
          })
          .finally(() => {
            this.loaderService.hide();
            this.isLoginProgress = false;
          });
        }
      })
      .catch(error => {
        this.loaderService.hide();
        this.isLoginProgress = false;
        this.showLoginError(error);
      });
    }
  }

  onVerify(verificationCode: string) {
    this.loaderService.show();
    this.authService.verifyLoginCode(this.verifyUser.account, this.verifyUser.username, this.verifyUser.password, verificationCode)
    .then(result => {
      return this.authService.loginToFirebase(result.token)
      .then(() => {
        if (!this.accountNameDontRemeber) { localStorage.setItem('vsightWorkflowAccountName', this.loginUser.account) }
        else { localStorage.removeItem('vsightWorkflowAccountName') }
      });
    })
    .catch(error => this.showLoginError(error, true))
    .finally(() => this.loaderService.hide());
  }

  showLoginError(error: any, verificationError: boolean = false) {
    if (error instanceof HttpErrorResponse) {
      if (error.error === 'wrong-username-password') {
        this.translateService.get('LOGIN.USERNAME-PASSWORD-ERROR').subscribe(result => {
          this.snackBarService.error(result);
        });
      } else if (error.error === 'account-disabled') {
        this.translateService.get('LOGIN.ACCOUNT-DISABLED', environment.design).subscribe(result => {
          this.snackBarService.error(result);
        });
        if (verificationError) { this.verification.open = false }
      } else if (error.error === 'no-workflow-license') {
        this.translateService.get('LOGIN.ACCOUNT-DISABLED', environment.design).subscribe(result => {
          this.snackBarService.error(result);
        });
        if (verificationError) { this.verification.open = false }
      } else if (error.error === 'invalid-verification-code') {
        this.translateService.get('LOGIN.INVALID-VERIFICATION-CODE').subscribe(result => {
          this.snackBarService.error(result);
        });
      } else {
        this.translateService.get('LOGIN.UNKNOWN-ERROR').subscribe(result => {
          this.snackBarService.error(result);
        });
      }
    } else if (error instanceof Error && error.message === 'wrong-username-password') {
      this.translateService.get('LOGIN.USERNAME-PASSWORD-ERROR').subscribe(result => {
        this.snackBarService.error(result);
      });
    } else {
      this.translateService.get('LOGIN.UNKNOWN-ERROR').subscribe(result => {
        this.snackBarService.error(result);
      });
    }
  }

  onTermsofService() {
    let url = window.location.origin+"/terms";

    const termsUrls = environment.design['termsUrls'];
    const defaultTermsLang = environment.design['defaultTermsLang'];
    if (termsUrls) {
      if (termsUrls[this.multilanguageService.currentLang.code]) {
        url = termsUrls[this.multilanguageService.currentLang.code];
      } else if (defaultTermsLang && termsUrls[defaultTermsLang]) {
        url = termsUrls[defaultTermsLang];
      }
    }
    window.open(url);
  }

  onPrivacyPolicy() {
    let url = window.location.origin+"/privacy";

    const privacyUrls = environment.design['privacyUrls'];
    const defaultPrivacyLang = environment.design['defaultPrivacyLang'];
    if (privacyUrls) {
      if (privacyUrls[this.multilanguageService.currentLang.code]) {
        url = privacyUrls[this.multilanguageService.currentLang.code];
      } else if (defaultPrivacyLang && privacyUrls[defaultPrivacyLang]) {
        url = privacyUrls[defaultPrivacyLang];
      }
    }
    window.open(url);
  }
}
